import {
  Component,
  HostListener,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import * as momentTz from 'moment-timezone';
import {
  CalendarOptions,
  DateSelectArg,
  EventClickArg,
  EventContentArg,
  EventInput
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  AdminBatchScheduleEvent,
  AdminBatchScheduleEventsRes,
  BatchScheduleEventDeleteType,
  BatchScheduleEventEditType,
  CLASS_CARD_TEXT,
  classCardColors,
  course,
  PotEvent,
  SUBJECT_NAMES,
  TIME_ZONES
} from '../../constants/constants';
import { GlobalApiService } from '../../services/global-api.service';
import { BookedClass } from '../../shared/class-overlap.util';
import {
  generateWeeklyOccurrences,
  parseTime12Hour,
  toLinkHref,
  toViewerTime,
  viewerTimeZone,
  viewerTimeZoneAbbr
} from '../../shared/class-schedule.util';
import {
  ClassDetailsComponent,
  ClassDetailsDialogData,
  ClassDetailsDialogResult
} from '../class-details/class-details.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '../confirm-dialog/confirm-dialog.component';
import {
  ClassFormComponent,
  ClassFormDialogData,
  ClassFormResult,
  ClassFormSchedule
} from '../class-form/class-form.component';
import { HasUnsavedChanges } from '../../services/unsaved-changes.guard';
import { auto } from '@popperjs/core';

// One dated class on the batch calendar; date and times are in the class's
// own time zone, and are converted to the viewer's before being shown
interface ClassOccurrence {
  scheduleIndex: number;
  bseId?: number;
  title: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string;
  timeZone: number;
  tutor: string;
  classUrl: string;
  subject: number;
  // 0 = hidden from the calendar, 1 = shown
  isDisabled: number;
  // Students see only the title (saved classes only)
  hideDetails?: boolean;
}

@Component({
  selector: 'app-calendar',
  templateUrl: './manage-classes.component.html',
  styleUrls: ['./manage-classes.component.scss']
})
export class ManageClassesComponent implements OnInit, HasUnsavedChanges {
  @ViewChild('fullCalendar') fullCalendarComponent!: FullCalendarComponent;
  @ViewChild('miniCalendar') miniCalendarComponent?: FullCalendarComponent;
  @Input('isAdminOrTutor') isAdminOrTutor: boolean = false;
  role: string = localStorage['roleId'];

  calendarEvents: EventInput[] = [];

  courses: course[] = [];
  selectedCourseId: number | null = null;

  screenType: 'create' | 'update' = 'create';
  // Shows the calendar
  showSchedule = false;
  students: any[] = [];
  selectedStudentIds: any[] = [];
  studentFilter = '';
  createOrUpdateBatchClicked = false;

  private removedCourses: any[] = [];
  private removedStudents: any[] = [];
  private availableStudentsCache: { key: string; list: any[] } | null = null;
  private removedSchedules: ClassFormSchedule[] = [];

  // Dated class events from the server, plus what they were generated from,
  // so unsaved schedule/date edits fall back to the recurring preview
  private serverEventsByScheduleId = new Map<
    number,
    AdminBatchScheduleEvent[]
  >();
  private savedScheduleTimings = new Map<number, string>();
  // Each saved schedule as loaded, to spot unsaved edits
  private savedScheduleSnapshots = new Map<number, string>();
  // Batch name, active flag and dates as last saved
  private savedBatchKey = '';
  // Lets the page be left right after a new batch is created
  private skipLeaveCheck = false;
  private savedDateRange = '';

  batchForm: FormGroup = new FormGroup({
    batchId: new FormControl(null),
    batchName: new FormControl('', [Validators.required]),
    isBatchActive: new FormControl(true),
    start_date: new FormControl(<any>null, [Validators.required]),
    end_date: new FormControl(<any>null, [Validators.required]),
    courses: new FormArray(<any>[]),
    students: new FormArray(<any>[]),
    schedules: new FormArray(<any>[])
  });

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay,listBatch'
    },
    views: {
      // Lists every class from batch start to end instead of a single month
      listBatch: {
        type: 'list',
        buttonText: 'List',
        visibleRange: (currentDate: Date) => this.batchVisibleRange(currentDate)
      }
    },
    buttonText: {
      today: 'Today',
      month: 'Month',
      week: 'Week',
      day: 'Day',
      list: 'List'
    },
    height: 'auto',
    // Classes are changed through the popover's Edit menu, not by dragging
    editable: false,
    selectable: true,
    unselectAuto: true,
    eventDisplay: 'block',
    dayMaxEvents: true,
    navLinks: true,
    select: (arg: DateSelectArg) => this.onRangeSelect(arg),
    eventClick: (arg: EventClickArg) => this.onEventClick(arg),
    eventContent: (arg: EventContentArg) => this.renderEventContent(arg),
    eventClassNames: (arg) => (arg.isPast ? ['class-event-past'] : []),
    // List rows take the subject colours from these, like the calendar cards
    eventDidMount: (arg) => {
      arg.el.style.setProperty('--class-accent', arg.event.borderColor);
      arg.el.style.setProperty('--class-tint', arg.event.backgroundColor);
    },
    datesSet: (arg) =>
      this.miniCalendarComponent?.getApi()?.gotoDate(arg.view.currentStart)
  };

  miniCalendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: { left: 'prev', center: 'title', right: 'next' },
    height: 'auto',
    fixedWeekCount: false,
    dayHeaderFormat: { weekday: 'narrow' },
    dateClick: (arg) =>
      this.fullCalendarComponent.getApi().gotoDate(arg.dateStr)
  };

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private globalApi: GlobalApiService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.savedBatchKey = this.batchDetailsKey();
    this.loadCourses();
    this.batchForm
      .get('start_date')
      ?.valueChanges.subscribe(() => this.buildCalendarEventsFromSchedules());
    this.batchForm
      .get('end_date')
      ?.valueChanges.subscribe(() => this.buildCalendarEventsFromSchedules());

    this.route.params.subscribe((params: Params) => {
      const batchId = parseInt(params['batchId'], 10);
      if (!isNaN(batchId)) {
        this.screenType = 'update';
        this.loadStudents();
        this.refreshBatchFromServer(batchId);
      }
    });
  }

  openClassFormCentered(): void {
    const startDate = this.batchForm.get('start_date')?.value;
    const endDate = this.batchForm.get('end_date')?.value;
    if (!startDate || !endDate) {
      this.snackBar.open(
        'Please select a batch start and end date first',
        undefined,
        {
          duration: 3000
        }
      );
      return;
    }
    this.openClassForm({
      startDate: this.toDateString(startDate),
      endDate: this.toDateString(endDate)
    });
  }

  createOrUpdateBatch(): void {
    if (this.createOrUpdateBatchClicked) {
      return;
    }
    if (
      this.batchForm.get('batchName')?.invalid ||
      this.coursesArray.length === 0 ||
      this.batchForm.get('start_date')?.invalid ||
      this.batchForm.get('end_date')?.invalid ||
      this.schedulesArray.invalid
    ) {
      this.batchForm.markAllAsTouched();
      this.snackBar.open(
        'Please fill in the batch name, at least one course, start/end date and complete all schedules',
        undefined,
        { duration: 4000 }
      );
      return;
    }

    this.createOrUpdateBatchClicked = true;
    const isCreate = this.screenType === 'create';
    const formValue = this.batchForm.value;
    const payload = {
      batchName: formValue.batchName,
      isBatchActive: formValue.isBatchActive ? 1 : 0,
      batchId: formValue.batchId,
      selectedCourses: [...formValue.courses, ...this.removedCourses],
      selectedStudents: [...formValue.students, ...this.removedStudents],
      start_date: moment(formValue.start_date).format('MM-DD-YYYY'),
      end_date: moment(formValue.end_date).format('MM-DD-YYYY'),
      schedules: [...formValue.schedules, ...this.removedSchedules].map(
        ({ repeatFrequency, ...schedule }) => ({
          ...schedule,
          day: this.toBackendDay(schedule.day),
          scheduleStartDate: this.toBackendDate(schedule.scheduleStartDate),
          scheduleEndDate: this.toBackendDate(schedule.scheduleEndDate),
          // Only a new schedule sets its repeat frequency
          ...(schedule.bschId == null && { repeatFrequency })
        })
      )
    };

    this.globalApi.createEditBatch(payload).subscribe({
      next: (response: any) => {
        const data = JSON.parse(response['data']);
        this.createOrUpdateBatchClicked = false;
        this.removedCourses = [];
        this.removedStudents = [];
        this.removedSchedules = [];
        this.snackBar.open(
          `Successfully ${isCreate ? 'created' : 'updated'} the batch`,
          undefined,
          { duration: 3000 }
        );
        if (isCreate) {
          this.skipLeaveCheck = true;
          this.router.navigate([`/edit-class/${data['batchId']}`]);
        } else {
          this.refreshBatchFromServer(data['batchId']);
        }
      },
      error: (error) => {
        this.createOrUpdateBatchClicked = false;
        if (
          error?.error?.msg ===
          'while creating a batch , batchname is not uniqueTest 1'
        ) {
          this.snackBar.open('Duplicate batch name', undefined, {
            duration: 4000
          });
          return;
        }
        this.snackBar.open(
          `Unable to ${isCreate ? 'create' : 'update'} the batch`,
          undefined,
          { duration: 4000 }
        );
      }
    });
  }

  private loadStudents(): void {
    this.globalApi.getAllStudents().subscribe({
      next: (response: any) => {
        const students = JSON.parse(response['data']);
        for (const student of students) {
          student['studentName'] =
            `${student['first_name']} ${student['last_name']}`;
        }
        this.students = students;
      },
      error: () => {
        this.snackBar.open('Unable to fetch students', undefined, {
          duration: 3000
        });
      }
    });
  }

  addStudents(): void {
    for (const studentId of this.selectedStudentIds) {
      const alreadySelected = this.studentsArray.controls.some(
        (control) => control.value.studentId === studentId
      );
      if (alreadySelected) {
        continue;
      }
      const student = this.students.find((s) => s.student_id === studentId);
      if (!student) {
        continue;
      }
      this.studentsArray.push(
        new FormGroup({
          bstId: new FormControl(null),
          isStudentActive: new FormControl(1),
          studentName: new FormControl(student.studentName),
          studentId: new FormControl(student.student_id)
        })
      );
    }
    this.selectedStudentIds = [];
  }

  removeStudent(index: number): void {
    const control = this.studentsArray.at(index) as FormGroup;
    if (control?.value?.bstId != null) {
      this.removedStudents.push({ ...control.value, isStudentActive: 0 });
    }
    this.studentsArray.removeAt(index);
  }

  get selectedStudents(): FormGroup[] {
    return this.studentsArray.controls as FormGroup[];
  }

  // Students not yet in the batch, for the picker. Cached so the picker
  // gets the same array until the batch changes
  get availableStudents(): any[] {
    const taken = this.selectedStudents.map((c) => c.value.studentId);
    const key = `${this.students.length}|${taken.join(',')}`;
    if (this.availableStudentsCache?.key !== key) {
      this.availableStudentsCache = {
        key,
        list: this.students.filter((s) => !taken.includes(s.student_id))
      };
    }
    return this.availableStudentsCache.list;
  }

  get filteredStudents(): FormGroup[] {
    const query = this.studentFilter.trim().toLowerCase();
    return query
      ? this.selectedStudents.filter((c) =>
          (c.value.studentName ?? '').toLowerCase().includes(query)
        )
      : this.selectedStudents;
  }

  // What the next save will change, e.g. ['2 classes added', '1 student removed']
  get unsavedChanges(): string[] {
    const schedules = this.schedulesArray.controls.map((c) => c.value);
    const counts: [number, string, string][] = [
      [schedules.filter((s) => s.bschId == null).length, 'class', 'added'],
      [
        schedules.filter(
          (s) =>
            s.bschId != null &&
            this.savedScheduleSnapshots.has(s.bschId) &&
            this.savedScheduleSnapshots.get(s.bschId) !== JSON.stringify(s)
        ).length,
        'class',
        'edited'
      ],
      [this.removedSchedules.length, 'class', 'removed'],
      [
        this.coursesArray.controls.filter((c) => c.value.bcId == null).length,
        'course',
        'added'
      ],
      [this.removedCourses.length, 'course', 'removed'],
      [
        this.selectedStudents.filter((c) => c.value.bstId == null).length,
        'student',
        'added'
      ],
      [this.removedStudents.length, 'student', 'removed']
    ];
    const changes = counts
      .filter(([count]) => count > 0)
      .map(([count, noun, action]) => {
        const plural = noun === 'class' ? 'classes' : `${noun}s`;
        return `${count} ${count === 1 ? noun : plural} ${action}`;
      });
    if (this.batchDetailsKey() !== this.savedBatchKey) {
      changes.unshift('batch details changed');
    }
    return changes;
  }

  private batchDetailsKey(): string {
    const { batchName, isBatchActive, start_date, end_date } =
      this.batchForm.value;
    const date = (d: Date | null) => (d ? this.toDateString(d) : '');
    return [
      batchName ?? '',
      !!isBatchActive,
      date(start_date),
      date(end_date)
    ].join('|');
  }

  hasUnsavedChanges(): boolean {
    return !this.skipLeaveCheck && this.unsavedChanges.length > 0;
  }

  // Closing or reloading the tab
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  trackByStudent(_: number, control: FormGroup): number {
    return control.value.studentId;
  }

  removeStudentControl(control: FormGroup): void {
    this.removeStudent(this.selectedStudents.indexOf(control));
  }

  studentInitials(name: string): string {
    return (name ?? '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  // A steady avatar colour per student
  studentAvatarClass(studentId: number): string {
    return `student-tile__avatar--${Math.abs(Number(studentId) || 0) % 6}`;
  }

  openClassForm(data: ClassFormDialogData, anchor?: MouseEvent): void {
    const dialogRef = this.dialog.open<
      ClassFormComponent,
      ClassFormDialogData,
      ClassFormResult
    >(ClassFormComponent, {
      maxHeight: '100vh',
      hasBackdrop: true,
      panelClass: 'popover-dialog',
      backdropClass: 'popover-backdrop',
      data: {
        ...data,
        ...this.batchDateRange(),
        bookedClasses: this.bookedClasses()
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.saved) {
        return;
      }
      this.addSchedules(result.schedules);
      this.snackBar.open('Class created', undefined, { duration: 3000 });
    });
  }

  private addSchedules(schedules: ClassFormSchedule[]): void {
    for (const schedule of schedules) {
      this.schedulesArray.push(
        new FormGroup({
          bschId: new FormControl(schedule.bschId),
          title: new FormControl(schedule.title),
          scheduleStartDate: new FormControl(schedule.scheduleStartDate),
          scheduleEndDate: new FormControl(schedule.scheduleEndDate),
          type: new FormControl(schedule.type, [Validators.required]),
          subject: new FormControl(schedule.subject, [Validators.required]),
          tutor: new FormControl(schedule.tutor, [Validators.required]),
          day: new FormControl(schedule.day, [Validators.required]),
          startTime: new FormControl(schedule.startTime, [Validators.required]),
          endTime: new FormControl(schedule.endTime, [Validators.required]),
          timeZone: new FormControl(schedule.timeZone, [Validators.required]),
          classUrl: new FormControl(schedule.classUrl, [Validators.required]),
          isScheduleActive: new FormControl(schedule.isScheduleActive),
          isDisabled: new FormControl(schedule.isDisabled),
          repeatFrequency: new FormControl(schedule.repeatFrequency ?? 1)
        })
      );
    }
    console.log('Batch schedules form array:', this.schedulesArray.value);
    this.buildCalendarEventsFromSchedules();
  }

  onRangeSelect(arg: DateSelectArg): void {
    const startDate = this.batchForm.get('start_date')?.value;
    const endDate = this.batchForm.get('end_date')?.value;
    if (!startDate || !endDate) {
      this.snackBar.open(
        'Please select a batch start and end date first',
        undefined,
        {
          duration: 3000
        }
      );
      arg.view.calendar.unselect();
      return;
    }
    const hasTime = !arg.allDay;
    const startTime = hasTime ? arg.startStr.slice(11, 16) : undefined;
    const endTime =
      hasTime && arg.endStr ? arg.endStr.slice(11, 16) : undefined;

    this.openClassForm(
      {
        startDate: this.toDateString(startDate),
        endDate: this.toDateString(endDate),
        startTime,
        endTime
      },
      arg.jsEvent ?? undefined
    );
    arg.view.calendar.unselect();
  }

  onEventClick(arg: EventClickArg): void {
    const index = this.scheduleIndexFromEventId(arg.event.id);
    if (index === null) {
      return;
    }
    const classEvent = this.serverEventFromEventId(index, arg.event.id);
    if (classEvent) {
      this.openClassEventDetailsPopover(index, classEvent, arg.event.title);
      return;
    }
    if (arg.event.start) {
      this.openScheduleDetailsPopover(index, arg.event.start);
    }
  }

  // Popover data: the class with its date and times in the viewer's time
  // zone; edits keep using the class's own time zone
  private viewerDetailsData(pot: PotEvent): ClassDetailsDialogData {
    if (!pot.startTime || !pot.endTime) {
      return { pot };
    }
    const endDate =
      pot.endTime <= pot.startTime
        ? this.addOneDay(pot.startDate)
        : pot.startDate;
    const start = toViewerTime(pot.startDate, pot.startTime, pot.timeZone);
    const end = toViewerTime(endDate, pot.endTime, pot.timeZone);
    return {
      pot: {
        ...pot,
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        startTime: start.format('HH:mm'),
        endTime: end.format('HH:mm')
      },
      timeZoneLabel: viewerTimeZoneAbbr(start)
    };
  }

  // "schedule-<index>-<bseId>" for saved classes, "schedule-<index>-d<date>"
  // for unsaved ones
  private scheduleIndexFromEventId(id: string): number | null {
    const match = /^schedule-(\d+)(?:-\d+|-d\d{8})?$/.exec(id);
    return match ? Number(match[1]) : null;
  }

  private serverEventFromEventId(
    index: number,
    id: string
  ): AdminBatchScheduleEvent | undefined {
    const match = /^schedule-\d+-(\d+)$/.exec(id);
    const bschId = this.schedulesArray.at(index)?.value?.bschId;
    if (!match || bschId == null) {
      return undefined;
    }
    const bseId = Number(match[1]);
    return this.serverEventsByScheduleId
      .get(bschId)
      ?.find((event) => event.bseId === bseId);
  }

  private classEventToPot(
    event: AdminBatchScheduleEvent,
    title: string
  ): PotEvent {
    const date = moment(event.classDate, 'MM-DD-YYYY').format('YYYY-MM-DD');
    return {
      id: `class-event-${event.bseId}`,
      bseId: event.bseId,
      // Shows the series menus (this / following / all) in the popover
      recurrenceId: `schedule-${event.bschId}`,
      title,
      startDate: date,
      endDate: date,
      startTime: parseTime12Hour(event.startTime),
      endTime: parseTime12Hour(event.endTime),
      tutor: event.tutor,
      location: event.classUrl || undefined,
      type: event.type,
      subject: event.subject,
      timeZone: event.timeZone,
      hideDetails: event.hideDetails === 1
    };
  }

  private openClassEventDetailsPopover(
    index: number,
    event: AdminBatchScheduleEvent,
    title: string
  ): void {
    const pot = this.classEventToPot(event, title);
    const dialogRef = this.dialog.open<
      ClassDetailsComponent,
      ClassDetailsDialogData,
      ClassDetailsDialogResult
    >(ClassDetailsComponent, {
      hasBackdrop: true,
      panelClass: 'popover-dialog',
      backdropClass: 'popover-backdrop',
      data: this.viewerDetailsData(pot)
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      if (result.action === 'duplicate') {
        this.duplicateSchedule(index);
        return;
      }
      if (result.action === 'delete') {
        const deleteType: BatchScheduleEventDeleteType =
          result.scope === 'series' ? 3 : result.scope === 'following' ? 2 : 1;
        this.confirmDeleteClassEvent(pot, deleteType);
        return;
      }
      const editType: BatchScheduleEventEditType =
        result.scope === 'following' ? 2 : 1;
      this.openClassEventEditDialog(index, pot, editType);
    });
  }

  // The schedule's saved classes on or after a date (YYYY-MM-DD)
  private seriesRangeFrom(
    index: number,
    fromDate: string
  ): { start: string; end: string; count: number } | undefined {
    const bschId = this.schedulesArray.at(index)?.value?.bschId;
    const dates = (this.serverEventsByScheduleId.get(bschId) ?? [])
      .map((event) =>
        moment(event.classDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
      )
      .filter((date) => date >= fromDate)
      .sort();
    return dates.length
      ? { start: dates[0], end: dates[dates.length - 1], count: dates.length }
      : undefined;
  }

  private openClassEventEditDialog(
    index: number,
    pot: PotEvent,
    editType: BatchScheduleEventEditType
  ): void {
    // Leave out the classes this edit replaces
    const bookedClasses = this.bookedClasses(
      (o) =>
        o.bseId === pot.bseId ||
        (editType === 2 && o.scheduleIndex === index && o.date >= pot.startDate)
    );
    const dialogRef = this.dialog.open<
      ClassFormComponent,
      ClassFormDialogData,
      ClassFormResult
    >(ClassFormComponent, {
      hasBackdrop: true,
      panelClass: 'popover-dialog',
      backdropClass: 'popover-backdrop',
      data: {
        startDate: pot.startDate,
        endDate: pot.endDate,
        pot,
        classEventEditType: editType,
        seriesRange:
          editType === 2
            ? this.seriesRangeFrom(index, pot.startDate)
            : undefined,
        bookedClasses
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.saved) {
        return;
      }
      this.snackBar.open('Changes saved', undefined, { duration: 3000 });
      this.refreshBatchFromServer(this.batchForm.get('batchId')?.value, true);
    });
  }

  private confirmDeleteClassEvent(
    pot: PotEvent,
    deleteType: BatchScheduleEventDeleteType
  ): void {
    const dialogData: ConfirmDialogData = {
      1: {
        title: 'Delete Class',
        message: `Are you sure you want to delete "${pot.title}" on ${pot.startDate}? Other occurrences are kept.`
      },
      2: {
        title: 'Delete This and Following',
        message: `Are you sure you want to delete "${pot.title}" on ${pot.startDate} and every future occurrence? Earlier occurrences are kept.`
      },
      3: {
        title: 'Delete Series',
        message: `Are you sure you want to delete every occurrence of "${pot.title}"? This will remove the entire recurring series.`
      }
    }[deleteType];

    const dialogRef = this.dialog.open<
      ConfirmDialogComponent,
      ConfirmDialogData,
      boolean
    >(ConfirmDialogComponent, { width: '400px', data: dialogData });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.globalApi
        .deleteBatchScheduleEvent({ bseId: pot.bseId!, deleteType })
        .subscribe({
          next: () => {
            this.snackBar.open(`Deleted "${pot.title}"`, undefined, {
              duration: 3000
            });
            this.refreshBatchFromServer(
              this.batchForm.get('batchId')?.value,
              true
            );
          },
          error: () => {
            this.snackBar.open('Unable to delete the class', undefined, {
              duration: 4000
            });
          }
        });
    });
  }

  // The schedule's own title, else its course and tutor
  private scheduleTitle(schedule: any): string {
    if (schedule.title) {
      return schedule.title;
    }
    const course = this.courses.find((c) => c.courseId === schedule.subject);
    return `${course?.courseName ?? 'Class'} - ${schedule.tutor}`;
  }

  // date is the class's date in the schedule's own time zone
  private scheduleToPot(schedule: any, date: string, index: number): PotEvent {
    return {
      id: `schedule-${index}`,
      title: this.scheduleTitle(schedule),
      startDate: date,
      endDate: date,
      startTime: schedule.startTime
        ? parseTime12Hour(schedule.startTime)
        : undefined,
      endTime: schedule.endTime ? parseTime12Hour(schedule.endTime) : undefined,
      tutor: schedule.tutor,
      location: schedule.classUrl || undefined,
      isActive: !!schedule.isScheduleActive && !schedule.isDisabled,
      type: schedule.type,
      subject: schedule.subject,
      timeZone: schedule.timeZone
    };
  }

  private openScheduleDetailsPopover(index: number, start: Date): void {
    const scheduleGroup = this.schedulesArray.at(index) as FormGroup;
    if (!scheduleGroup) {
      return;
    }
    const schedule = scheduleGroup.value;
    const zone = TIME_ZONES[schedule.timeZone - 1] ?? viewerTimeZone();
    const pot = this.scheduleToPot(
      schedule,
      momentTz(start).tz(zone).format('YYYY-MM-DD'),
      index
    );

    const dialogRef = this.dialog.open<
      ClassDetailsComponent,
      ClassDetailsDialogData,
      ClassDetailsDialogResult
    >(ClassDetailsComponent, {
      hasBackdrop: true,
      panelClass: 'popover-dialog',
      backdropClass: 'popover-backdrop',
      data: this.viewerDetailsData(pot)
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      if (result.action === 'edit') {
        this.openScheduleEditDialog(index, pot);
      } else if (result.action === 'delete') {
        this.confirmDeleteSchedule(index, pot.title);
      } else if (result.action === 'duplicate') {
        this.duplicateSchedule(index);
      }
    });
  }

  private openScheduleEditDialog(index: number, pot: PotEvent): void {
    const schedule = this.schedulesArray.at(index)?.value ?? {};
    const dialogRef = this.dialog.open<
      ClassFormComponent,
      ClassFormDialogData,
      ClassFormResult
    >(ClassFormComponent, {
      hasBackdrop: true,
      panelClass: 'popover-dialog',
      backdropClass: 'popover-backdrop',
      data: {
        startDate: schedule.scheduleStartDate || pot.startDate,
        endDate: schedule.scheduleEndDate || pot.endDate,
        ...this.batchDateRange(),
        clickedDate: pot.startDate,
        pot,
        bookedClasses: this.bookedClasses((o) => o.scheduleIndex === index)
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.saved) {
        return;
      }
      this.replaceSchedule(index, result.schedules);
      this.snackBar.open('Changes saved', undefined, { duration: 3000 });
    });
  }

  private replaceSchedule(index: number, schedules: ClassFormSchedule[]): void {
    if (!schedules.length) {
      return;
    }
    const control = this.schedulesArray.at(index) as FormGroup;
    if (!control) {
      this.addSchedules(schedules);
      return;
    }
    const [schedule, ...rest] = schedules;
    // repeatFrequency is left as is: the edit form doesn't show it
    control.patchValue({
      title: schedule.title,
      scheduleStartDate: schedule.scheduleStartDate,
      scheduleEndDate: schedule.scheduleEndDate,
      type: schedule.type,
      subject: schedule.subject,
      tutor: schedule.tutor,
      day: schedule.day,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      timeZone: schedule.timeZone,
      classUrl: schedule.classUrl,
      isScheduleActive: schedule.isScheduleActive,
      isDisabled: schedule.isDisabled
    });
    if (rest.length) {
      this.addSchedules(rest);
    } else {
      this.buildCalendarEventsFromSchedules();
    }
  }

  private duplicateSchedule(index: number): void {
    const source = this.schedulesArray.at(index) as FormGroup;
    if (!source) {
      return;
    }
    this.schedulesArray.push(
      new FormGroup({
        bschId: new FormControl(null),
        title: new FormControl(source.value.title),
        scheduleStartDate: new FormControl(source.value.scheduleStartDate),
        scheduleEndDate: new FormControl(source.value.scheduleEndDate),
        type: new FormControl(source.value.type, [Validators.required]),
        subject: new FormControl(source.value.subject, [Validators.required]),
        tutor: new FormControl(source.value.tutor, [Validators.required]),
        day: new FormControl(source.value.day, [Validators.required]),
        startTime: new FormControl(source.value.startTime, [
          Validators.required
        ]),
        endTime: new FormControl(source.value.endTime, [Validators.required]),
        timeZone: new FormControl(source.value.timeZone, [Validators.required]),
        classUrl: new FormControl(source.value.classUrl, [Validators.required]),
        isScheduleActive: new FormControl(source.value.isScheduleActive),
        isDisabled: new FormControl(source.value.isDisabled),
        repeatFrequency: new FormControl(source.value.repeatFrequency ?? 1)
      })
    );
    this.buildCalendarEventsFromSchedules();
    this.snackBar.open('Schedule duplicated', undefined, { duration: 3000 });
  }

  private confirmDeleteSchedule(index: number, title: string): void {
    const dialogRef = this.dialog.open<
      ConfirmDialogComponent,
      ConfirmDialogData,
      boolean
    >(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Class',
        message: `Are you sure you want to delete this Class? "${title}" will be permanently removed.`
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }
      const control = this.schedulesArray.at(index) as FormGroup;
      if (control?.value?.bschId != null) {
        this.removedSchedules.push({
          ...control.value,
          isScheduleActive: 0
        });
      }
      this.schedulesArray.removeAt(index);
      this.buildCalendarEventsFromSchedules();
      this.snackBar.open(`Deleted "${title}"`, undefined, { duration: 3000 });
    });
  }

  // keepLocalChanges re-applies what hasn't been saved yet, for reloads
  // after a single class is edited or deleted through the event API
  private refreshBatchFromServer(
    batchId: number,
    keepLocalChanges = false
  ): void {
    this.globalApi.getBatchScheduleEventsByBatchId(batchId).subscribe({
      next: (response: AdminBatchScheduleEventsRes) => {
        const data: any = response.data;
        const localChanges = keepLocalChanges
          ? this.captureLocalChanges()
          : null;

        this.batchForm.patchValue({
          batchId,
          batchName: data['batchName'],
          isBatchActive: data['isBatchActive'] === 1,
          start_date: new Date(data['startDate'].split('-').join('/')),
          end_date: new Date(data['endDate'].split('-').join('/'))
        });

        this.coursesArray.clear();
        for (const c of data['courses'].filter(
          (course: any) => course['isCourseActive'] === 1
        )) {
          this.coursesArray.push(
            new FormGroup({
              bcId: new FormControl(c['bcId']),
              courseId: new FormControl(c['courseId'], [Validators.required]),
              courseName: new FormControl(c['courseName'], [
                Validators.required
              ]),
              isCourseActive: new FormControl(1)
            })
          );
        }

        this.studentsArray.clear();
        for (const s of data['students'].filter(
          (student: any) => student['isStudentActive'] === 1
        )) {
          this.studentsArray.push(
            new FormGroup({
              bstId: new FormControl(s['bstId']),
              isStudentActive: new FormControl(1),
              studentName: new FormControl(s['studentName']),
              studentId: new FormControl(s['studentId'])
            })
          );
        }

        this.schedulesArray.clear();
        for (const schedule of data['schedules']) {
          this.schedulesArray.push(
            new FormGroup({
              bschId: new FormControl(schedule['bschId']),
              title: new FormControl(schedule['title'] ?? ''),
              scheduleStartDate: new FormControl(
                this.fromBackendDate(schedule['scheduleStartDate'])
              ),
              scheduleEndDate: new FormControl(
                this.fromBackendDate(schedule['scheduleEndDate'])
              ),
              type: new FormControl(schedule['type'], [Validators.required]),
              subject: new FormControl(schedule['subject'], [
                Validators.required
              ]),
              tutor: new FormControl(schedule['tutor'], [Validators.required]),
              day: new FormControl(this.fromBackendDay(schedule['day']), [
                Validators.required
              ]),
              startTime: new FormControl(schedule['startTime'], [
                Validators.required
              ]),
              endTime: new FormControl(schedule['endTime'], [
                Validators.required
              ]),
              timeZone: new FormControl(schedule['timeZone'], [
                Validators.required
              ]),
              classUrl: new FormControl(schedule['classUrl'], [
                Validators.required
              ]),
              isScheduleActive: new FormControl(
                schedule['isScheduleActive'] ?? 1
              ),
              isDisabled: new FormControl(schedule['isDisabled'] ?? 1),
              repeatFrequency: new FormControl(schedule['repeatFrequency'] ?? 1)
            })
          );
        }

        this.serverEventsByScheduleId.clear();
        for (const event of (data['events'] ??
          []) as AdminBatchScheduleEvent[]) {
          const events = this.serverEventsByScheduleId.get(event.bschId) ?? [];
          events.push(event);
          this.serverEventsByScheduleId.set(event.bschId, events);
        }
        this.savedScheduleTimings.clear();
        this.savedScheduleSnapshots.clear();
        for (const control of this.schedulesArray.controls) {
          this.savedScheduleTimings.set(
            control.value.bschId,
            this.scheduleTimingKey(control.value)
          );
          this.savedScheduleSnapshots.set(
            control.value.bschId,
            JSON.stringify(control.value)
          );
        }
        this.savedDateRange = this.currentDateRange();
        this.savedBatchKey = this.batchDetailsKey();

        if (localChanges) {
          this.applyLocalChanges(localChanges);
        }
        this.buildCalendarEventsFromSchedules();
      },
      error: () => {
        this.snackBar.open('Unable to refresh batch data', undefined, {
          duration: 3000
        });
      }
    });
  }

  // Everything on the page that the next batch save would send
  private captureLocalChanges() {
    const { batchName, isBatchActive, start_date, end_date } =
      this.batchForm.value;
    return {
      batch: { batchName, isBatchActive, start_date, end_date },
      newCourses: this.coursesArray.controls
        .map((c) => c.value)
        .filter((c) => c.bcId == null),
      newStudents: this.selectedStudents
        .map((c) => c.value)
        .filter((s) => s.bstId == null),
      newSchedules: this.schedulesArray.controls
        .map((c) => c.value)
        .filter((s) => s.bschId == null),
      editedSchedules: this.schedulesArray.controls
        .map((c) => c.value)
        .filter(
          (s) =>
            s.bschId != null &&
            this.savedScheduleSnapshots.has(s.bschId) &&
            this.savedScheduleSnapshots.get(s.bschId) !== JSON.stringify(s)
        )
    };
  }

  private applyLocalChanges(
    local: ReturnType<ManageClassesComponent['captureLocalChanges']>
  ): void {
    this.batchForm.patchValue(local.batch, { emitEvent: false });

    // Removals are still listed in removed*, so take them out again
    const removedCourseIds = this.removedCourses.map((c) => c.bcId);
    this.removeControls(this.coursesArray, (c) =>
      removedCourseIds.includes(c.bcId)
    );
    const removedStudentIds = this.removedStudents.map((s) => s.bstId);
    this.removeControls(this.studentsArray, (s) =>
      removedStudentIds.includes(s.bstId)
    );
    const removedScheduleIds = this.removedSchedules.map((s) => s.bschId);
    this.removeControls(this.schedulesArray, (s) =>
      removedScheduleIds.includes(s.bschId)
    );

    for (const course of local.newCourses) {
      if (
        !this.coursesArray.controls.some(
          (c) => c.value.courseId === course.courseId
        )
      ) {
        this.coursesArray.push(
          new FormGroup({
            bcId: new FormControl(null),
            courseId: new FormControl(course.courseId, [Validators.required]),
            courseName: new FormControl(course.courseName, [
              Validators.required
            ]),
            isCourseActive: new FormControl(1)
          })
        );
      }
    }

    for (const student of local.newStudents) {
      if (
        !this.selectedStudents.some(
          (c) => c.value.studentId === student.studentId
        )
      ) {
        this.studentsArray.push(
          new FormGroup({
            bstId: new FormControl(null),
            isStudentActive: new FormControl(1),
            studentName: new FormControl(student.studentName),
            studentId: new FormControl(student.studentId)
          })
        );
      }
    }

    for (const edited of local.editedSchedules) {
      this.schedulesArray.controls
        .find((c) => c.value.bschId === edited.bschId)
        ?.patchValue(edited);
    }

    this.addSchedules(local.newSchedules);
  }

  private removeControls(array: FormArray, match: (value: any) => boolean) {
    for (let i = array.length - 1; i >= 0; i--) {
      if (match(array.at(i).value)) {
        array.removeAt(i);
      }
    }
  }

  // Every dated class: the server's classes for saved, unchanged schedules,
  // otherwise the schedule repeated across the batch dates
  private classOccurrences(): ClassOccurrence[] {
    const startDate = this.batchForm.get('start_date')?.value;
    const endDate = this.batchForm.get('end_date')?.value;
    if (!startDate || !endDate) {
      return [];
    }
    const batchStart = this.toDateString(startDate);
    const batchEnd = this.toDateString(endDate);
    const datesUnchanged = this.currentDateRange() === this.savedDateRange;

    return this.schedulesArray.controls.flatMap(
      (control, index): ClassOccurrence[] => {
        const schedule = control.value;
        const title = this.scheduleTitle(schedule);
        const serverEvents = this.serverEventsByScheduleId.get(schedule.bschId);
        const unchanged =
          datesUnchanged &&
          this.savedScheduleTimings.get(schedule.bschId) ===
            this.scheduleTimingKey(schedule);

        if (serverEvents && unchanged) {
          return serverEvents.map((event) => ({
            scheduleIndex: index,
            bseId: event.bseId,
            title: event.title || title,
            date: moment(event.classDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
            startTime: parseTime12Hour(event.startTime),
            endTime: parseTime12Hour(event.endTime),
            timeZone: event.timeZone,
            tutor: event.tutor,
            classUrl: event.classUrl,
            subject: event.subject,
            // Hidden when either the schedule or the class is disabled
            isDisabled:
              schedule.isDisabled === 0 || event.isDisabled === 0 ? 0 : 1,
            hideDetails: event.hideDetails === 1
          }));
        }

        // A schedule runs over its own dates, kept inside the batch
        const scheduleStart =
          schedule.scheduleStartDate > batchStart
            ? schedule.scheduleStartDate
            : batchStart;
        const scheduleEnd =
          schedule.scheduleEndDate && schedule.scheduleEndDate < batchEnd
            ? schedule.scheduleEndDate
            : batchEnd;
        return generateWeeklyOccurrences(
          scheduleStart,
          scheduleEnd,
          [schedule.day],
          366,
          schedule.repeatFrequency ?? 1
        ).map((date) => ({
          scheduleIndex: index,
          title,
          date,
          startTime: parseTime12Hour(schedule.startTime),
          endTime: parseTime12Hour(schedule.endTime),
          timeZone: schedule.timeZone,
          tutor: schedule.tutor,
          classUrl: schedule.classUrl,
          subject: schedule.subject,
          isDisabled: schedule.isDisabled
        }));
      }
    );
  }

  // Classes that run past midnight end on the next day
  private occurrenceEndDate(occurrence: ClassOccurrence): string {
    return occurrence.endTime <= occurrence.startTime
      ? this.addOneDay(occurrence.date)
      : occurrence.date;
  }

  // Start and end of a class in the viewer's time zone
  private occurrenceInViewerTime(occurrence: ClassOccurrence) {
    return {
      start: toViewerTime(
        occurrence.date,
        occurrence.startTime,
        occurrence.timeZone
      ),
      end: toViewerTime(
        this.occurrenceEndDate(occurrence),
        occurrence.endTime,
        occurrence.timeZone
      )
    };
  }

  private buildCalendarEventsFromSchedules(): void {
    this.calendarEvents = this.classOccurrences()
      .filter((occurrence) => occurrence.isDisabled !== 0)
      .map((occurrence) => {
      const { start, end } = this.occurrenceInViewerTime(occurrence);
      // FullCalendar matches events to its previous ones by id, so every
      // class needs its own, or a schedule's classes collapse into one
      return {
        id:
          occurrence.bseId != null
            ? `schedule-${occurrence.scheduleIndex}-${occurrence.bseId}`
            : `schedule-${occurrence.scheduleIndex}-d${occurrence.date.replace(/-/g, '')}`,
        title: occurrence.title,
        start: start.toDate(),
        end: end.toDate(),
        ...this.eventAppearance(occurrence)
      };
    });
  }

  // The batch's classes, in the viewer's time zone, for the class form's
  // tutor clash check
  private bookedClasses(
    skip: (occurrence: ClassOccurrence) => boolean = () => false
  ): BookedClass[] {
    return this.classOccurrences()
      .filter((occurrence) => !skip(occurrence))
      .map((occurrence) => {
        const { start, end } = this.occurrenceInViewerTime(occurrence);
        return {
          title: occurrence.title,
          tutor: occurrence.tutor,
          startDate: start.format('YYYY-MM-DD'),
          endDate: end.format('YYYY-MM-DD'),
          startTime: start.format('HH:mm'),
          endTime: end.format('HH:mm')
        };
      });
  }

  private eventAppearance(source: {
    tutor: string;
    classUrl: string;
    subject: number;
    hideDetails?: boolean;
  }): EventInput {
    const colors = classCardColors(source.subject, source.hideDetails);
    return {
      classNames: ['class-event-card'],
      backgroundColor: colors.tint,
      borderColor: colors.accent,
      textColor: CLASS_CARD_TEXT,
      extendedProps: {
        tutor: source.tutor,
        classUrl: source.classUrl,
        subject: SUBJECT_NAMES[source.subject] ?? '',
        hideDetails: !!source.hideDetails
      }
    };
  }

  // Week/Day boxes are as tall as the class: 28px per half hour (see the
  // .fc-timegrid-slot height in the SCSS), and each line of text is 15px
  private static readonly TIMEGRID_PX_PER_MINUTE = 28 / 30;
  private static readonly TIMEGRID_LINE_PX = 15;
  private static readonly TIMEGRID_PADDING_PX = 6;

  // How many lines of text fit in a Week/Day class box
  private timeGridLines(start: Date | null, end: Date | null): number {
    if (!start || !end) {
      return 1;
    }
    const minutes = (end.getTime() - start.getTime()) / 60000;
    const height = minutes * ManageClassesComponent.TIMEGRID_PX_PER_MINUTE;
    return Math.max(
      1,
      Math.floor(
        (height - ManageClassesComponent.TIMEGRID_PADDING_PX) /
          ManageClassesComponent.TIMEGRID_LINE_PX
      )
    );
  }

  // Month: time, title, "Subject · Course", tutor and link on their own
  // lines. Week/Day: as many of those as the class length has room for.
  // List: has its own time column, so the rest goes on one line.
  private renderEventContent(arg: EventContentArg) {
    const isList = arg.view.type.startsWith('list');
    const isTimeGrid = arg.view.type.startsWith('timeGrid');
    const { tutor, classUrl, subject, hideDetails } = arg.event.extendedProps;
    const course = this.selectedCourses
      .map((c) => c.value.courseName)
      .join(', ');
    const time = this.formatTimeRange(arg.event.start, arg.event.end);
    const title = arg.event.title;
    const heading = [subject, course].filter(Boolean).join(' · ');
    const link = (classUrl ?? '').replace(/^https?:\/\//, '');

    const root = document.createElement('div');
    root.className = isList ? 'class-event class-event--list' : 'class-event';

    type Item = { value: string; icon?: string; href?: string | null };
    const addRow = (className: string, items: Item[]) => {
      const shown = items.filter((item) => item.value);
      if (!shown.length) {
        return;
      }
      const row = document.createElement('div');
      row.className = `class-event__row ${className}`;
      for (const item of shown) {
        if (item.icon) {
          const iconEl = document.createElement('span');
          iconEl.className = 'material-icons class-event__icon';
          iconEl.textContent = item.icon;
          row.appendChild(iconEl);
        }
        const text = document.createElement(item.href ? 'a' : 'span');
        text.className = 'class-event__text';
        text.textContent = item.value;
        if (item.href) {
          const anchor = text as HTMLAnchorElement;
          anchor.href = item.href;
          anchor.target = '_blank';
          anchor.rel = 'noopener';
          anchor.classList.add('class-event__link');
          // Open the link without also opening the class popover
          anchor.addEventListener('click', (event) => event.stopPropagation());
        }
        row.appendChild(text);
      }
      root.appendChild(row);
    };

    const timeItem: Item = { value: time };
    // Marks the classes students see as title only
    const titleItem: Item = {
      value: title,
      icon: hideDetails ? 'visibility_off' : undefined
    };
    const headingItem: Item = { value: heading };
    const tutorItem: Item = { value: tutor, icon: 'person' };
    const linkItem: Item = {
      value: link,
      icon: 'link',
      href: toLinkHref(classUrl)
    };

    if (isList) {
      addRow('class-event__title', [titleItem]);
      addRow('class-event__heading', [headingItem]);
      addRow('class-event__meta', [tutorItem]);
      addRow('class-event__meta', [linkItem]);
    } else if (!isTimeGrid) {
      addRow('class-event__time', [timeItem]);
      addRow('class-event__title', [titleItem]);
      addRow('class-event__heading', [headingItem]);
      addRow('class-event__meta', [tutorItem]);
      addRow('class-event__meta', [linkItem]);
    } else {
      const lines = this.timeGridLines(arg.event.start, arg.event.end);
      root.classList.add(`class-event--lines-${Math.min(lines, 5)}`);
      // Room to spare (2h+): the five lines get extra spacing
      if (lines >= 6) {
        root.classList.add('class-event--roomy');
      }
      if (lines === 1) {
        const startTime = arg.event.start
          ? moment(arg.event.start).format('h:mm A')
          : '';
        addRow('class-event__title', [
          {
            value: [startTime, title].filter(Boolean).join(' · '),
            icon: titleItem.icon
          }
        ]);
      } else {
        addRow('class-event__time', [timeItem]);
        addRow('class-event__title', [titleItem]);
        if (lines >= 3) {
          addRow('class-event__heading', [headingItem]);
        }
        if (lines === 4) {
          addRow('class-event__meta', [tutorItem, linkItem]);
        } else if (lines >= 5) {
          addRow('class-event__meta', [tutorItem]);
          addRow('class-event__meta', [linkItem]);
        }
      }
    }
    root.title = [
      time,
      title,
      hideDetails ? 'Students see only the title' : '',
      heading,
      tutor,
      classUrl
    ]
      .filter(Boolean)
      .join('\n');
    return { domNodes: [root] };
  }

  // "7:00 – 8:00 AM", or "11:30 AM – 12:30 PM" across noon/midnight
  private formatTimeRange(start: Date | null, end: Date | null): string {
    if (!start) {
      return '';
    }
    const from = moment(start);
    if (!end) {
      return from.format('h:mm A');
    }
    const to = moment(end);
    const range =
      from.format('A') === to.format('A')
        ? `${from.format('h:mm')} – ${to.format('h:mm A')}`
        : `${from.format('h:mm A')} – ${to.format('h:mm A')}`;
    return `${range} ${viewerTimeZoneAbbr(start)}`;
  }

  // Backend schedule days are 1-7 (Mon-Sun); the calendar uses 0-6 (Sun-Sat)
  private toBackendDay(day: number): number {
    return day === 0 ? 7 : day;
  }

  private fromBackendDay(day: number): number {
    return day === 7 ? 0 : day;
  }

  private batchVisibleRange(currentDate: Date): { start: string; end: string } {
    const startDate = this.batchForm.get('start_date')?.value;
    const endDate = this.batchForm.get('end_date')?.value;
    if (startDate && endDate) {
      return {
        start: this.toDateString(startDate),
        end: this.addOneDay(this.toDateString(endDate))
      };
    }
    const monthStart = moment(currentDate).startOf('month');
    return {
      start: monthStart.format('YYYY-MM-DD'),
      end: monthStart.add(1, 'month').format('YYYY-MM-DD')
    };
  }

  private scheduleTimingKey(schedule: any): string {
    return `${schedule.day}|${schedule.startTime}|${schedule.endTime}|${
      schedule.repeatFrequency ?? 1
    }|${schedule.scheduleStartDate ?? ''}|${schedule.scheduleEndDate ?? ''}`;
  }

  private batchDateRange(): { batchStartDate?: string; batchEndDate?: string } {
    const startDate = this.batchForm.get('start_date')?.value;
    const endDate = this.batchForm.get('end_date')?.value;
    return {
      batchStartDate: startDate ? this.toDateString(startDate) : undefined,
      batchEndDate: endDate ? this.toDateString(endDate) : undefined
    };
  }

  // Schedule dates are YYYY-MM-DD here and MM-DD-YYYY in the API
  private toBackendDate(date: string | null | undefined): string | null {
    return date ? moment(date, 'YYYY-MM-DD').format('MM-DD-YYYY') : null;
  }

  private fromBackendDate(date: string | null | undefined): string | null {
    if (!date) {
      return null;
    }
    const parsed = moment(date, ['MM-DD-YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
  }

  private currentDateRange(): string {
    const startDate = this.batchForm.get('start_date')?.value;
    const endDate = this.batchForm.get('end_date')?.value;
    return startDate && endDate
      ? `${this.toDateString(startDate)}|${this.toDateString(endDate)}`
      : '';
  }

  private loadCourses(): void {
    this.globalApi.getCourses().subscribe({
      next: (response) => {
        this.courses = response?.data ?? [];
      },
      error: () => {
        this.snackBar.open('Unable to fetch courses', undefined, {
          duration: 3000
        });
      }
    });
  }

  get coursesArray(): FormArray {
    return this.batchForm.get('courses') as FormArray;
  }

  get studentsArray(): FormArray {
    return this.batchForm.get('students') as FormArray;
  }

  get schedulesArray(): FormArray {
    return this.batchForm.get('schedules') as FormArray;
  }

  get selectedCourses(): FormGroup[] {
    return this.coursesArray.controls as FormGroup[];
  }

  onCourseSelect(courseId: number | null): void {
    if (courseId == null) {
      return;
    }
    const course = this.courses.find((c) => c.courseId === courseId);
    if (!course) {
      return;
    }
    const alreadySelected = this.coursesArray.controls.some(
      (control) => control.value.courseId === courseId
    );
    if (!alreadySelected) {
      this.coursesArray.push(
        new FormGroup({
          bcId: new FormControl(null),
          courseId: new FormControl(course.courseId, [Validators.required]),
          courseName: new FormControl(course.courseName, [Validators.required]),
          isCourseActive: new FormControl(1)
        })
      );
    }
    this.selectedCourseId = null;
  }

  removeCourse(index: number): void {
    const control = this.coursesArray.at(index) as FormGroup;
    if (control?.value?.bcId != null) {
      this.removedCourses.push({ ...control.value, isCourseActive: 0 });
    }
    this.coursesArray.removeAt(index);
  }

  private shiftDate(dateStr: string, days: number): string {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }

  private addOneDay(dateStr: string): string {
    return this.shiftDate(dateStr, 1);
  }

  private toDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
